'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import EditUserAbout from '@/app/[locale]/(worlds)/profile/components/EditUserAbout';
import Loader from '@/components/Loader';
import ViverseButton from '@/components/ViverseButton';
import { Form } from '@/components/ui/form';
import { useConfigsContext } from '@/context/ConfigsContext';
import { formSchema, FormSchema, socialLinksSchema } from '@/schemas/media';
import { updateProfileMeByCmsAccountId } from '@/server/lib/profile';
import { SocialLink } from '@/types/store';
function UserAbout() {
  const t = useTranslations();
  const {
    store,
    setStore
  } = useConfigsContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  const socialLinks = store?.profile?.socialLinks;
  const defaultSocialLinks = useMemo(() => socialLinks?.reduce((acc, link) => {
    acc[link.key] = link.value;
    return acc;
  }, {} as Record<keyof typeof socialLinksSchema.shape, string>), [socialLinks]);
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bio: store?.profile?.bio || '',
      socialLinks: defaultSocialLinks,
      enableSocialLinks: store?.profile?.showSocialLinks || false
    }
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!htcToken || !cmsAccountId) {
      throw new Error('No HTC token or CMS account ID');
    }
    setIsSaving(true);
    try {
      const socialLinks_0 = Object.entries(data.socialLinks).map(([key, value]) => ({
        key,
        value
      })).filter(({
        value: value_0
      }) => value_0) as SocialLink[];
      const res = await updateProfileMeByCmsAccountId(cmsAccountId, {
        bio: data.bio,
        show_social_links: data.enableSocialLinks,
        social_links: socialLinks_0
      });
      if (res?.status === 200 && res?.results) {
        setStore(value_1 => ({
          ...value_1,
          profile: {
            ...value_1.profile!,
            bio: data.bio,
            showSocialLinks: data.enableSocialLinks,
            socialLinks: socialLinks_0
          }
        }));
      } else {
        throw new Error('Failed to update profile');
      }
    } catch {
      // error surfaced via toast in the calling logic
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };
  const onCancel = () => {
    setIsEditing(false);
    form.reset({
      bio: store?.profile?.bio || '',
      socialLinks: defaultSocialLinks,
      enableSocialLinks: store?.profile?.showSocialLinks || false
    });
  };
  return <section id="about" className="flex scroll-mt-40 flex-col justify-between px-4 md:px-8 lg:px-18">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full">
          <div className="flex w-full flex-col gap-4 md:gap-8">
            <div className="flex min-h-9 items-center justify-between">
              <h2 className="header-5 md:header-4">{t('profile-page.anchors.about')}</h2>
              {isEditing && <div className="flex gap-2">
                  <ViverseButton variant="secondary" className="min-w-fit" disabled={isSaving} onClick={onCancel}>
                    {t('button.cancel')}
                  </ViverseButton>
                  <ViverseButton className="min-w-fit" type="submit" disabled={isSaving}>
                    {t('edit-profile.save')}
                  </ViverseButton>
                </div>}
            </div>

            <div className="relative flex flex-col gap-5">
              {isSaving && <div className="absolute inset-0 z-floating flex cursor-wait items-center justify-center">
                  <Loader />
                </div>}
              {isEditing ? <EditUserAbout /> : <div className="flex flex-col gap-5">
                  <p className="body-2 whitespace-pre-line md:text-[18px]">
                    {store?.profile?.bio || t('profile-page.about.bio-placeholder')}
                  </p>
                  <ViverseButton variant="icon3" className="w-fit" onClick={() => setIsEditing(true)}>
                    <Plus className="stroke-vive-primary-20 mr-2 size-6" />
                    {t('profile-page.about.add-bio')}
                  </ViverseButton>
                </div>}
            </div>
          </div>
        </form>
      </Form>
    </section>;
}
export default UserAbout;